/**
 * Unified Projects Page
 *
 * Shows all projects across all apps (Site DNA, Flux EV, Flux EMS, Cold Storage, PhD)
 * Click a project row to navigate to ProjectOverviewPage (/all-projects/:id)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Chip,
    Tooltip,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    alpha,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
    FormGroup,
    TableRow,
    TableCell,
    IconButton,
    Snackbar,
    Switch,
} from '@mui/material';
import {
    Add,
    Search,
    SolarPower,
    ElectricCar,
    BatteryChargingFull,
    AcUnit,
    Science,
    LocationOn,
    Folder as FolderIcon,
    Delete as DeleteIcon,
    ContentCopy,
    ElectricBolt,
} from '@mui/icons-material';
import {
    useAuth,
    useThemeMode,
    vortxColors,
    retroColors,
    useUnifiedProjects,
    API_BASE_URL,
    ProjectTableContainer,
    ProjectTableHead,
    ProjectTableHeaderCell,
    ProjectTableBody,
} from '@vortx/shared-ui';
import { useRetroContextSafe, vortxTypography } from '@vortx/shared-ui';

// App icons and colors mapping
const APP_CONFIG = {
    site_dna: {
        label: 'Site DNA',
        icon: SolarPower,
        color: '#ffa500',
        route: '/projects'
    },
    flux_ev: {
        label: 'Flux EV',
        icon: ElectricCar,
        color: '#4CAF50',
        route: '/ev'
    },
    flux_ems: {
        label: 'Flux EMS',
        icon: BatteryChargingFull,
        color: vortxColors.gold,
        route: '/ems'
    },
    flux_cold: {
        label: 'Flux COLD',
        icon: AcUnit,
        color: '#2196F3',
        route: '/cold-storage'
    },
    flux_phd: {
        label: 'Flux PhD',
        icon: Science,
        color: '#9b59b6',
        route: '/phd'
    }
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

// Check if an app is enabled on a project
// site_dna is stored as an array of systems, other apps as a single config object
const isAppEnabled = (project, appKey) => {
    const appConfig = project.apps?.[appKey];
    if (!appConfig) return false;
    if (Array.isArray(appConfig)) {
        return appConfig.some(s => s.enabled);
    }
    return !!appConfig.enabled;
};

// Extract tariff display name from project meters
const getTariffDisplay = (project) => {
    const tariffName = project.meters?.main?.tariff?.name;
    if (tariffName) return tariffName;
    return null;
};

// Check if project has a load profile
const hasLoadProfile = (project) => {
    const loadProfile = project.meters?.main?.load_profile;
    if (!loadProfile) return false;
    // Check for 8760 data or any load profile indicator
    if (loadProfile.hours) return loadProfile.hours;
    if (loadProfile.data && Array.isArray(loadProfile.data)) return loadProfile.data.length;
    return !!loadProfile.source || !!loadProfile.filename;
};

export default function ProjectsPage() {
    const { currentUser, userProfile } = useAuth();
    const { mode } = useThemeMode();
    const navigate = useNavigate();
    const { projects, loading, error, loadProjects, createProject, deleteProject, duplicateProject } = useUnifiedProjects(null);
    const { setActiveProject } = useRetroContextSafe();
    const isDemo = userProfile?.is_demo;
    const isSuperAdmin = userProfile?.is_super_admin;

    const [searchQuery, setSearchQuery] = useState('');
    const [filterApp, setFilterApp] = useState('all');

    // Create project dialog state
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [newProjectDescription, setNewProjectDescription] = useState('');
    const [newProjectAddress, setNewProjectAddress] = useState('');
    const [selectedApps, setSelectedApps] = useState({
        site_dna: true,
        flux_ev: false,
        flux_ems: false,
        flux_cold: false,
        flux_phd: false
    });
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');

    // Duplicate state
    const [duplicating, setDuplicating] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });

    // Delete dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const isRetro = mode === 'retro';

    // Filter projects based on search and app filter
    const filteredProjects = projects.filter(project => {
        const matchesSearch = !searchQuery ||
            project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (project.description && project.description.toLowerCase().includes(searchQuery.toLowerCase()));

        const matchesApp = filterApp === 'all' ||
            isAppEnabled(project, filterApp);

        return matchesSearch && matchesApp;
    });

    const handleOpenProject = (project) => {
        setActiveProject(project);
        navigate(`/all-projects/${project.id}`);
    };

    const handleDeleteProject = async () => {
        if (!projectToDelete) return;
        setDeleting(true);
        try {
            await deleteProject(projectToDelete.id);
            setDeleteDialogOpen(false);
            setProjectToDelete(null);
            loadProjects();
        } catch (err) {
            console.error('Failed to delete project:', err);
        } finally {
            setDeleting(false);
        }
    };

    const handleDuplicateProject = async (project) => {
        setDuplicating(project.id);
        try {
            await duplicateProject(project.id);
            setSnackbar({ open: true, message: `Duplicated "${project.name}"` });
            loadProjects();
        } catch (err) {
            console.error('Failed to duplicate project:', err);
            setSnackbar({ open: true, message: `Failed to duplicate: ${err.message}` });
        } finally {
            setDuplicating(null);
        }
    };

    const handleDemoToggle = async (projectId, checked) => {
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_BASE_URL}/api/projects/v1/projects/${projectId}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ demo: checked }),
            });
            if (!response.ok) throw new Error('Failed to update demo flag');
            loadProjects();
        } catch (err) {
            console.error('Failed to toggle demo flag:', err);
            setSnackbar({ open: true, message: `Failed to toggle demo: ${err.message}` });
        }
    };

    const handleCreateProject = () => {
        setCreateDialogOpen(true);
    };

    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
        setNewProjectName('');
        setNewProjectDescription('');
        setNewProjectAddress('');
        setSelectedApps({
            site_dna: true,
            flux_ev: false,
            flux_ems: false,
            flux_cold: false,
            flux_phd: false
        });
        setCreateError('');
    };

    const handleToggleApp = (app) => {
        setSelectedApps(prev => ({
            ...prev,
            [app]: !prev[app]
        }));
    };

    const handleSubmitCreate = async () => {
        if (!newProjectName.trim()) {
            setCreateError('Project name is required');
            return;
        }

        const hasSelectedApp = Object.values(selectedApps).some(enabled => enabled);
        if (!hasSelectedApp) {
            setCreateError('Please select at least one app to enable');
            return;
        }

        setCreating(true);
        setCreateError('');

        try {
            const appsConfig = {};
            Object.entries(selectedApps).forEach(([app, enabled]) => {
                if (enabled) {
                    appsConfig[app] = { enabled: true, config: {} };
                }
            });

            let location;
            const addressTrimmed = newProjectAddress.trim();
            if (addressTrimmed) {
                try {
                    const token = await currentUser.getIdToken();
                    const geoRes = await fetch(`${API_BASE_URL}/api/projects/v1/geocode`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ address: addressTrimmed }),
                    });
                    if (geoRes.ok) {
                        const geoData = await geoRes.json();
                        location = { address: addressTrimmed, lat: geoData.lat, lon: geoData.lon };
                    } else {
                        location = { address: addressTrimmed, lat: 0, lon: 0 };
                    }
                } catch (geoErr) {
                    console.warn('Geocoding failed, saving address without coordinates:', geoErr);
                    location = { address: addressTrimmed, lat: 0, lon: 0 };
                }
            }

            await createProject({
                name: newProjectName.trim(),
                description: newProjectDescription.trim() || undefined,
                ...(location && { location }),
                apps: appsConfig
            });

            handleCloseCreateDialog();
            loadProjects();
        } catch (err) {
            console.error('Failed to create project:', err);
            setCreateError(err.message || 'Failed to create project');
        } finally {
            setCreating(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                p: { xs: 2, sm: 3, md: 4 },
                background: isRetro
                    ? 'transparent'
                    : `linear-gradient(135deg, ${alpha(vortxColors.white, 0.95)} 0%, ${alpha(vortxColors.warmStone, 0.1)} 100%)`
            }}
        >
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontFamily: vortxTypography.heading,
                                fontWeight: 800,
                                color: isRetro ? vortxColors.white : vortxColors.charcoal,
                                mb: 1,
                                textShadow: isRetro ? `0 0 20px ${alpha(vortxColors.gold, 0.5)}` : 'none',
                                letterSpacing: '-0.02em'
                            }}
                        >
                            {isRetro ? '>> PROJECTS' : 'Projects'}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: isRetro ? vortxColors.stone : vortxColors.charcoal,
                                opacity: 0.9,
                                maxWidth: 600
                            }}
                        >
                            Unified view of all your projects across Site DNA, Flux EV, Flux EMS, and more.
                        </Typography>
                    </Box>

                    {!isDemo && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleCreateProject}
                            sx={{
                                background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
                                color: isRetro ? '#FFFFFF' : vortxColors.green,
                                fontWeight: 700,
                                px: 3,
                                py: 1.5,
                                fontSize: '0.95rem',
                                textTransform: 'none',
                                fontFamily: vortxTypography.heading,
                                boxShadow: isRetro
                                    ? `0 0 20px ${alpha(vortxColors.gold, 0.4)}`
                                    : '0 4px 12px rgba(178, 140, 80, 0.3)',
                                '&:hover': {
                                    background: `linear-gradient(135deg, #c99f6a 0%, ${vortxColors.gold} 100%)`,
                                    boxShadow: isRetro
                                        ? `0 0 30px ${alpha(vortxColors.gold, 0.6)}`
                                        : '0 6px 20px rgba(178, 140, 80, 0.5)'
                                }
                            }}
                        >
                            {isRetro ? 'NEW PROJECT' : 'New Project'}
                        </Button>
                    )}
                </Box>

                {/* Search and Filters */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        placeholder={isRetro ? "SEARCH PROJECTS..." : "Search projects..."}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        sx={{
                            flexGrow: 1,
                            minWidth: 200,
                            maxWidth: 400
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: vortxColors.stone }} />
                                </InputAdornment>
                            )
                        }}
                    />

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            size="small"
                            variant={filterApp === 'all' ? 'contained' : 'outlined'}
                            onClick={() => setFilterApp('all')}
                            sx={{
                                color: filterApp === 'all'
                                    ? (isRetro ? '#FFFFFF' : vortxColors.green)
                                    : (isRetro ? vortxColors.white : vortxColors.charcoal),
                                backgroundColor: filterApp === 'all' ? vortxColors.gold : 'transparent',
                                borderColor: vortxColors.gold,
                                '&:hover': {
                                    backgroundColor: filterApp === 'all'
                                        ? '#c99f6a'
                                        : alpha(vortxColors.gold, 0.1)
                                }
                            }}
                        >
                            All Apps
                        </Button>
                        {Object.entries(APP_CONFIG).map(([app, config]) => {
                            const IconComponent = config.icon;
                            return (
                                <Button
                                    key={app}
                                    size="small"
                                    variant={filterApp === app ? 'contained' : 'outlined'}
                                    startIcon={<IconComponent />}
                                    onClick={() => setFilterApp(app)}
                                    sx={{
                                        color: filterApp === app
                                            ? '#FFFFFF'
                                            : (isRetro ? vortxColors.white : vortxColors.charcoal),
                                        backgroundColor: filterApp === app ? config.color : 'transparent',
                                        borderColor: config.color,
                                        '&:hover': {
                                            backgroundColor: filterApp === app
                                                ? config.color
                                                : alpha(config.color, 0.1)
                                        }
                                    }}
                                >
                                    {config.label}
                                </Button>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress
                        sx={{
                            color: vortxColors.gold,
                            filter: isRetro ? `drop-shadow(0 0 10px ${vortxColors.gold})` : 'none'
                        }}
                    />
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Empty State */}
            {!loading && filteredProjects.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            color: isRetro ? vortxColors.stone : vortxColors.charcoal,
                            mb: 2,
                            fontFamily: vortxTypography.heading
                        }}
                    >
                        {searchQuery || filterApp !== 'all'
                            ? 'No projects match your filters'
                            : (isRetro ? '>> NO PROJECTS FOUND' : 'No projects yet')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: vortxColors.stone, mb: 3 }}>
                        {searchQuery || filterApp !== 'all'
                            ? 'Try adjusting your search or filters'
                            : 'Create your first project to get started'}
                    </Typography>
                    {!searchQuery && filterApp === 'all' && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleCreateProject}
                            sx={{
                                background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
                                color: isRetro ? '#FFFFFF' : vortxColors.green
                            }}
                        >
                            Create Project
                        </Button>
                    )}
                </Box>
            )}

            {/* Projects Table */}
            {!loading && filteredProjects.length > 0 && (
                <ProjectTableContainer>
                    <ProjectTableHead>
                        <ProjectTableHeaderCell>Project</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Location</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Tariff</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Apps</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell align="right">Updated</ProjectTableHeaderCell>
                        {isSuperAdmin && (
                            <ProjectTableHeaderCell align="center" sx={{ width: 70 }}>Demo</ProjectTableHeaderCell>
                        )}
                        {!isDemo && (
                            <ProjectTableHeaderCell align="center" sx={{ width: 90 }}>
                                {/* Actions */}
                            </ProjectTableHeaderCell>
                        )}
                    </ProjectTableHead>
                    <ProjectTableBody>
                        {filteredProjects.map(project => {
                            const enabledApps = Object.entries(project.apps || {})
                                .filter(([appKey]) => isAppEnabled(project, appKey))
                                .map(([app]) => app);

                            const tariffDisplay = getTariffDisplay(project);
                            const loadInfo = hasLoadProfile(project);

                            return (
                                <TableRow
                                    key={project.id}
                                    onClick={() => handleOpenProject(project)}
                                    sx={{
                                        cursor: 'pointer',
                                        borderLeft: '3px solid transparent',
                                        transition: 'all 0.15s ease',
                                        '&:hover': {
                                            bgcolor: isRetro
                                                ? 'rgba(178, 140, 80, 0.12)'
                                                : alpha(vortxColors.gold, 0.08),
                                            borderLeftColor: vortxColors.gold,
                                        },
                                    }}
                                >
                                    {/* Project Name */}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <FolderIcon sx={{
                                                fontSize: 18,
                                                color: isRetro ? vortxColors.gold : vortxColors.teal,
                                                opacity: 0.7
                                            }} />
                                            <Typography sx={{
                                                fontFamily: isRetro ? vortxTypography.mono : vortxTypography.heading,
                                                fontWeight: 600,
                                                fontSize: isRetro ? '0.85rem' : '0.95rem',
                                                color: isRetro ? vortxColors.white : vortxColors.charcoal,
                                                textTransform: isRetro ? 'uppercase' : 'none',
                                            }}>
                                                {project.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>

                                    {/* Location */}
                                    <TableCell>
                                        {project.location?.address ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <LocationOn sx={{ fontSize: 14, color: vortxColors.teal, opacity: 0.6 }} />
                                                <Typography sx={{
                                                    fontFamily: vortxTypography.mono,
                                                    fontSize: '0.75rem',
                                                    color: vortxColors.stone,
                                                    maxWidth: 200,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {project.location.address}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography sx={{
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                                color: vortxColors.stone,
                                                opacity: 0.4,
                                            }}>
                                                —
                                            </Typography>
                                        )}
                                    </TableCell>

                                    {/* Tariff */}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {tariffDisplay ? (
                                                <Chip
                                                    icon={<ElectricBolt />}
                                                    label={tariffDisplay}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: isRetro
                                                            ? alpha(vortxColors.gold, 0.12)
                                                            : alpha(vortxColors.gold, 0.08),
                                                        color: isRetro ? vortxColors.white : vortxColors.charcoal,
                                                        fontFamily: vortxTypography.mono,
                                                        fontWeight: 500,
                                                        fontSize: '0.65rem',
                                                        height: 22,
                                                        maxWidth: 160,
                                                        '& .MuiChip-label': {
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        },
                                                        '& .MuiChip-icon': {
                                                            color: vortxColors.gold,
                                                            fontSize: 12,
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <Typography sx={{
                                                    fontFamily: vortxTypography.mono,
                                                    fontSize: '0.7rem',
                                                    color: vortxColors.stone,
                                                    opacity: 0.4,
                                                }}>
                                                    No tariff
                                                </Typography>
                                            )}
                                            {loadInfo && (
                                                <Tooltip title={typeof loadInfo === 'number' ? `${loadInfo.toLocaleString()} hrs load data` : 'Load profile attached'}>
                                                    <ElectricBolt sx={{
                                                        fontSize: 14,
                                                        color: vortxColors.gold,
                                                        opacity: 0.6,
                                                    }} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>

                                    {/* App Badges */}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {enabledApps.map(app => {
                                                const config = APP_CONFIG[app];
                                                const IconComponent = config?.icon;
                                                return (
                                                    <Chip
                                                        key={app}
                                                        icon={IconComponent ? <IconComponent /> : undefined}
                                                        label={config?.label || app}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: isRetro
                                                                ? alpha(config?.color || vortxColors.teal, 0.15)
                                                                : alpha(config?.color || vortxColors.teal, 0.1),
                                                            color: isRetro ? vortxColors.white : vortxColors.charcoal,
                                                            fontFamily: vortxTypography.body,
                                                            fontWeight: 600,
                                                            fontSize: '0.6rem',
                                                            height: 22,
                                                            '& .MuiChip-icon': {
                                                                color: config?.color || vortxColors.teal,
                                                                fontSize: 12,
                                                            }
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    </TableCell>

                                    {/* Updated Date */}
                                    <TableCell align="right">
                                        <Typography sx={{
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.7rem',
                                            color: vortxColors.stone,
                                        }}>
                                            {formatDate(project.updated_at)}
                                        </Typography>
                                    </TableCell>

                                    {/* Demo Toggle */}
                                    {isSuperAdmin && (
                                        <TableCell align="center" sx={{ width: 70 }} onClick={(e) => e.stopPropagation()}>
                                            <Switch
                                                checked={!!project.demo}
                                                onChange={(e) => handleDemoToggle(project.id, e.target.checked)}
                                                size="small"
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: vortxColors.gold,
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: vortxColors.gold,
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                    )}

                                    {/* Actions */}
                                    {!isDemo && (
                                        <TableCell align="center" sx={{ width: 90 }}>
                                            <Tooltip title="Duplicate project">
                                                <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={duplicating === project.id}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDuplicateProject(project);
                                                    }}
                                                    sx={{
                                                        color: isRetro ? alpha(vortxColors.gold, 0.5) : alpha(vortxColors.teal, 0.5),
                                                        '&:hover': {
                                                            color: isRetro ? vortxColors.gold : vortxColors.teal,
                                                            backgroundColor: isRetro
                                                                ? alpha(vortxColors.gold, 0.1)
                                                                : alpha(vortxColors.teal, 0.08),
                                                        },
                                                    }}
                                                >
                                                    {duplicating === project.id
                                                        ? <CircularProgress size={14} sx={{ color: vortxColors.gold }} />
                                                        : <ContentCopy sx={{ fontSize: 16 }} />}
                                                </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Delete project">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setProjectToDelete(project);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                    sx={{
                                                        color: isRetro ? 'rgba(255, 107, 107, 0.5)' : 'rgba(211, 47, 47, 0.4)',
                                                        '&:hover': {
                                                            color: isRetro ? '#ff6b6b' : '#d32f2f',
                                                            backgroundColor: isRetro
                                                                ? 'rgba(255, 107, 107, 0.1)'
                                                                : 'rgba(211, 47, 47, 0.08)',
                                                        },
                                                    }}
                                                >
                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </ProjectTableBody>
                </ProjectTableContainer>
            )}

            {/* Stats Footer */}
            {!loading && filteredProjects.length > 0 && (
                <Box
                    sx={{
                        mt: 4,
                        pt: 3,
                        borderTop: `1px solid ${isRetro ? vortxColors.darkGreen : alpha(vortxColors.teal, 0.2)}`,
                        display: 'flex',
                        gap: 3,
                        flexWrap: 'wrap'
                    }}
                >
                    <Typography variant="caption" sx={{
                        color: vortxColors.stone,
                        fontFamily: vortxTypography.mono,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                    }}>
                        {isRetro ? '>> TOTAL:' : 'Total Projects:'} {filteredProjects.length}
                    </Typography>
                    {Object.entries(APP_CONFIG).map(([app, config]) => {
                        const count = filteredProjects.filter(p => isAppEnabled(p, app)).length;
                        if (count === 0) return null;
                        return (
                            <Typography key={app} variant="caption" sx={{
                                color: config.color,
                                fontFamily: vortxTypography.mono,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                            }}>
                                {config.label}: {count}
                            </Typography>
                        );
                    })}
                </Box>
            )}

            {/* Create Project Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={handleCloseCreateDialog}
                maxWidth="sm"
                fullWidth
                sx={{ zIndex: 1500 }}
                PaperProps={{
                    sx: {
                        background: isRetro
                            ? `linear-gradient(135deg, ${retroColors.bg} 0%, ${retroColors.surface} 100%)`
                            : `linear-gradient(135deg, ${vortxColors.white} 0%, ${alpha(vortxColors.warmStone, 0.1)} 100%)`,
                        border: isRetro ? `1px solid ${alpha(vortxColors.gold, 0.3)}` : 'none',
                        boxShadow: isRetro
                            ? `0 0 30px ${alpha(vortxColors.gold, 0.2)}`
                            : '0 8px 32px rgba(0,0,0,0.12)'
                    }
                }}
            >
                <DialogTitle sx={{
                    fontFamily: vortxTypography.heading,
                    fontWeight: 700,
                    color: isRetro ? vortxColors.gold : vortxColors.charcoal,
                    borderBottom: `1px solid ${isRetro ? alpha(vortxColors.gold, 0.2) : alpha(vortxColors.teal, 0.2)}`,
                    pb: 2
                }}>
                    {isRetro ? '>> NEW PROJECT' : 'Create New Project'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                            autoFocus
                            label={isRetro ? 'PROJECT NAME' : 'Project Name'}
                            value={newProjectName}
                            onChange={(e) => setNewProjectName(e.target.value)}
                            fullWidth
                            required
                            error={!!createError && !newProjectName.trim()}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: isRetro ? vortxColors.stone : vortxColors.charcoal,
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body
                                },
                                '& .MuiOutlinedInput-root': {
                                    color: isRetro ? vortxColors.white : vortxColors.charcoal,
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                    '& fieldset': { borderColor: isRetro ? alpha(vortxColors.gold, 0.3) : alpha(vortxColors.teal, 0.3) },
                                    '&:hover fieldset': { borderColor: isRetro ? alpha(vortxColors.gold, 0.5) : alpha(vortxColors.teal, 0.5) },
                                    '&.Mui-focused fieldset': { borderColor: isRetro ? vortxColors.gold : vortxColors.teal }
                                }
                            }}
                        />

                        <TextField
                            label={isRetro ? 'PROJECT DESCRIPTION' : 'Description'}
                            value={newProjectDescription}
                            onChange={(e) => setNewProjectDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: isRetro ? vortxColors.stone : vortxColors.charcoal,
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body
                                },
                                '& .MuiOutlinedInput-root': {
                                    color: isRetro ? vortxColors.white : vortxColors.charcoal,
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                    '& fieldset': { borderColor: isRetro ? alpha(vortxColors.gold, 0.3) : alpha(vortxColors.teal, 0.3) },
                                    '&:hover fieldset': { borderColor: isRetro ? alpha(vortxColors.gold, 0.5) : alpha(vortxColors.teal, 0.5) },
                                    '&.Mui-focused fieldset': { borderColor: isRetro ? vortxColors.gold : vortxColors.teal }
                                }
                            }}
                        />

                        <TextField
                            label={isRetro ? 'SITE ADDRESS' : 'Site Address'}
                            placeholder="e.g. 123 Main St, Los Angeles, CA 90001"
                            value={newProjectAddress}
                            onChange={(e) => setNewProjectAddress(e.target.value)}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LocationOn sx={{ color: isRetro ? alpha(vortxColors.gold, 0.5) : alpha(vortxColors.teal, 0.5), fontSize: 20 }} />
                                    </InputAdornment>
                                )
                            }}
                            helperText={isRetro ? 'COORDINATES AUTO-RESOLVED' : 'Coordinates will be resolved automatically'}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: isRetro ? vortxColors.stone : vortxColors.charcoal,
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body
                                },
                                '& .MuiOutlinedInput-root': {
                                    color: isRetro ? vortxColors.white : vortxColors.charcoal,
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                    '& fieldset': { borderColor: isRetro ? alpha(vortxColors.gold, 0.3) : alpha(vortxColors.teal, 0.3) },
                                    '&:hover fieldset': { borderColor: isRetro ? alpha(vortxColors.gold, 0.5) : alpha(vortxColors.teal, 0.5) },
                                    '&.Mui-focused fieldset': { borderColor: isRetro ? vortxColors.gold : vortxColors.teal }
                                },
                                '& .MuiFormHelperText-root': {
                                    color: isRetro ? alpha(vortxColors.stone, 0.6) : alpha(vortxColors.charcoal, 0.5),
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                    fontSize: '0.7rem',
                                    textTransform: isRetro ? 'uppercase' : 'none'
                                }
                            }}
                        />

                        <Box>
                            <Typography variant="subtitle2" sx={{
                                mb: 2,
                                color: isRetro ? vortxColors.stone : vortxColors.charcoal,
                                fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                textTransform: isRetro ? 'uppercase' : 'none',
                                letterSpacing: isRetro ? '0.05em' : 'normal'
                            }}>
                                {isRetro ? 'ENABLE APPS:' : 'Enable Apps'}
                            </Typography>
                            <FormGroup>
                                {Object.entries(APP_CONFIG).map(([app, config]) => {
                                    const IconComponent = config.icon;
                                    return (
                                        <FormControlLabel
                                            key={app}
                                            control={
                                                <Checkbox
                                                    checked={selectedApps[app]}
                                                    onChange={() => handleToggleApp(app)}
                                                    sx={{
                                                        color: isRetro ? alpha(config.color, 0.5) : config.color,
                                                        '&.Mui-checked': { color: config.color }
                                                    }}
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <IconComponent sx={{ fontSize: 20, color: config.color }} />
                                                    <Typography sx={{
                                                        fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                                        color: isRetro ? vortxColors.white : vortxColors.charcoal
                                                    }}>
                                                        {config.label}
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{
                                                py: 0.5,
                                                '&:hover': { backgroundColor: alpha(config.color, 0.05) }
                                            }}
                                        />
                                    );
                                })}
                            </FormGroup>
                        </Box>

                        {createError && (
                            <Alert severity="error" sx={{
                                backgroundColor: isRetro ? alpha('#ff6b6b', 0.1) : undefined,
                                color: isRetro ? '#ff6b6b' : undefined,
                                border: isRetro ? '1px solid rgba(255, 107, 107, 0.3)' : 'none',
                                '& .MuiAlert-icon': { color: isRetro ? '#ff6b6b' : undefined }
                            }}>
                                {createError}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{
                    borderTop: `1px solid ${isRetro ? alpha(vortxColors.gold, 0.2) : alpha(vortxColors.teal, 0.2)}`,
                    px: 3,
                    py: 2
                }}>
                    <Button
                        onClick={handleCloseCreateDialog}
                        disabled={creating}
                        sx={{
                            color: isRetro ? vortxColors.stone : vortxColors.gray,
                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                            textTransform: isRetro ? 'uppercase' : 'none',
                            '&:hover': { backgroundColor: alpha(vortxColors.stone, 0.1) }
                        }}
                    >
                        {isRetro ? 'ABORT' : 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleSubmitCreate}
                        disabled={creating || !newProjectName.trim()}
                        variant="contained"
                        sx={{
                            background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
                            color: isRetro ? '#FFFFFF' : vortxColors.green,
                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                            textTransform: isRetro ? 'uppercase' : 'none',
                            fontWeight: 600,
                            px: 3,
                            '&:hover': { background: `linear-gradient(135deg, #c99f6a 0%, #b28c50 100%)` },
                            '&.Mui-disabled': { opacity: 0.5 }
                        }}
                    >
                        {creating ? (isRetro ? 'CREATING...' : 'Creating...') : (isRetro ? 'DEPLOY' : 'Create Project')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => { setDeleteDialogOpen(false); setProjectToDelete(null); }}
                maxWidth="xs"
                fullWidth
                sx={{ zIndex: 1500 }}
                PaperProps={{
                    sx: {
                        background: isRetro
                            ? `linear-gradient(135deg, ${retroColors.bg} 0%, ${retroColors.surface} 100%)`
                            : vortxColors.white,
                        border: isRetro ? '1px solid rgba(255, 107, 107, 0.3)' : 'none',
                    }
                }}
            >
                <DialogTitle sx={{
                    fontFamily: vortxTypography.heading,
                    fontWeight: 700,
                    color: isRetro ? '#ff6b6b' : '#d32f2f',
                    borderBottom: `1px solid ${isRetro ? 'rgba(255, 107, 107, 0.2)' : 'rgba(211, 47, 47, 0.2)'}`,
                    pb: 2
                }}>
                    {isRetro ? '>> DELETE PROJECT' : 'Delete Project'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography sx={{
                        color: isRetro ? vortxColors.white : vortxColors.charcoal,
                        fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                        fontSize: '0.9rem',
                        mb: 1,
                    }}>
                        Are you sure you want to delete{' '}
                        <strong>{projectToDelete?.name}</strong>?
                    </Typography>
                    <Typography sx={{
                        color: isRetro ? vortxColors.stone : vortxColors.gray,
                        fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                        fontSize: '0.8rem',
                    }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{
                    borderTop: `1px solid ${isRetro ? 'rgba(255, 107, 107, 0.2)' : 'rgba(211, 47, 47, 0.2)'}`,
                    px: 3,
                    py: 2
                }}>
                    <Button
                        onClick={() => { setDeleteDialogOpen(false); setProjectToDelete(null); }}
                        disabled={deleting}
                        sx={{
                            color: isRetro ? vortxColors.stone : vortxColors.gray,
                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                            textTransform: isRetro ? 'uppercase' : 'none',
                        }}
                    >
                        {isRetro ? 'ABORT' : 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleDeleteProject}
                        disabled={deleting}
                        variant="contained"
                        sx={{
                            backgroundColor: '#d32f2f',
                            color: '#FFFFFF',
                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                            textTransform: isRetro ? 'uppercase' : 'none',
                            fontWeight: 600,
                            px: 3,
                            '&:hover': { backgroundColor: '#b71c1c' },
                            '&.Mui-disabled': { opacity: 0.5 }
                        }}
                    >
                        {deleting ? (isRetro ? 'DELETING...' : 'Deleting...') : (isRetro ? 'CONFIRM DELETE' : 'Delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Duplicate Feedback Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ open: false, message: '' })}
                message={snackbar.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
}
